import React, { useState } from 'react';
import {
  Box, Container, Typography, Link, useMediaQuery, useTheme, Button,
  IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <Box component="header"
         sx={{
           borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
           backgroundColor: '#ffffff',
           boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
           width: '100%'
         }}>
      <Container maxWidth="lg" sx={{ py: { xs: 0.75, sm: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          {/* Logo */}
          <Link
            onClick={() => isAuthenticated ? navigate('/home') : navigate('/login')}
            underline="none"
            sx={{ display: 'flex', cursor: 'pointer', flexShrink: 0, mr: 4 }}
          >
            <Box component="img"
                 src="/magna-logo-official-dark.svg"
                 alt="Magna Legal Services"
                 sx={{ height: { xs: 28, sm: 40 }, maxWidth: { xs: '130px', sm: '180px' } }} />
          </Link>

          {/* Title */}
          {!isMobile && (
            <Typography variant="h6" color="primary" component="div" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
              Records Portal
            </Typography>
          )}

          {/* Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              {isAuthenticated && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    color={isActive('/home') ? "primary" : "inherit"}
                    onClick={() => navigate('/home')}
                    sx={{ fontWeight: isActive('/home') ? 'bold' : 'normal', minWidth: 'auto' }}
                  >
                    Dashboard
                  </Button>
                  <Button
                    color={location.pathname.startsWith('/chat') ? "primary" : "inherit"}
                    onClick={() => navigate('/chat')}
                    sx={{ fontWeight: location.pathname.startsWith('/chat') ? 'bold' : 'normal', minWidth: 'auto' }}
                  >
                    Chat
                  </Button>
                  <Button
                    color={isActive('/account-settings') ? "primary" : "inherit"}
                    onClick={() => navigate('/account-settings')}
                    sx={{ fontWeight: isActive('/account-settings') ? 'bold' : 'normal', minWidth: 'auto' }}
                  >
                    Settings
                  </Button>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {isAuthenticated && (
                  <Box
                    component="button"
                    onClick={logout}
                    className="btn btn-secondary"
                    sx={{
                      fontSize: { sm: '0.75rem', md: '0.875rem' },
                      py: { sm: 1, md: 1.5 }, px: { sm: 2, md: 3 },
                      color: '#ED7420', backgroundColor: 'transparent', border: '1px solid #ED7420',
                      transition: 'all 0.3s ease', cursor: 'pointer',
                      '&:hover': { color: '#fff !important', backgroundColor: '#ff6a00 !important', border: '1px solid #ff6a00 !important' },
                    }}>
                    Logout
                  </Box>
                )}
                <Link href="https://magnals.com/contact-magna-legal-services/" underline="none" target="_blank">
                  <Box component="button"
                       className="btn btn-secondary"
                       sx={{
                         fontSize: { sm: '0.75rem', md: '0.875rem' },
                         py: { sm: 1, md: 1.5 }, px: { sm: 2, md: 3 },
                         color: '#ED7420', backgroundColor: 'transparent', border: '1px solid #ED7420',
                         transition: 'all 0.3s ease',
                         '&:hover': { color: '#fff !important', backgroundColor: '#ff6a00 !important', border: '1px solid #ff6a00 !important' },
                       }}>
                    Contact Us
                  </Box>
                </Link>
              </Box>
            </Box>
          )}
          {isMobile && (
            <IconButton color="inherit" edge="end" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Container>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}
              sx={{ '& .MuiDrawer-paper': { width: 280 } }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 500 }}>Records Portal</Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <Divider />
        {isAuthenticated && (
          <>
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNavigate('/home')} selected={isActive('/home')}>
                  <ListItemText primary="Dashboard" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNavigate('/chat')} selected={location.pathname.startsWith('/chat')}>
                  <ListItemText primary="Chat" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNavigate('/account-settings')} selected={isActive('/account-settings')}>
                  <ListItemText primary="Settings" />
                </ListItemButton>
              </ListItem>
            </List>
            <Divider />
          </>
        )}
        <List>
          {isAuthenticated && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => { logout(); setMobileMenuOpen(false); }}>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton component="a" href="https://magnals.com/contact-magna-legal-services/" target="_blank">
              <ListItemText primary="Contact Us" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </Box>
  );
};

export default Header;
